// @givmo/auth — re-authenticating an ALREADY signed-in user (AS-WEB3).
//
// Every other flow in this package ESTABLISHES a session. This one proves the live session still
// belongs to the person at the keyboard. The backend's step-up gate reads `auth_time` off the
// Firebase ID token and refuses a sensitive action once that proof is stale; Firebase's
// `reauthenticate*` primitives mint a token carrying a fresh `auth_time`, which is what lets the
// refused action succeed on retry.
//
// ⚠ DO NOT substitute the sign-in path. Calling `signInWithEmailAndPassword` / `signInWithStrategy`
// again also refreshes `auth_time`, and on a money surface it is the wrong primitive twice over:
//   1. Sign-in accepts ANY account's credentials and SWAPS the session, so the retried action would
//      execute as whoever just signed in — on a payout screen that is a different organisation's
//      admin completing a bank change. `reauthenticate*` is bound to the `user` passed in and rejects
//      a different account with `auth/user-mismatch`.
//   2. The apps' sign-in actions carry their own tails (`applyRememberMe` persistence, post-sign-in
//      routing). Proving identity must not silently change how long the session is remembered or
//      navigate away from the work in progress.
//
// POPUP-ONLY, deliberately — no redirect fallback, even though `signInWithStrategy` has one. A
// step-up interrupts work already in progress (bank details typed into a form, awaiting retry); the
// redirect handshake navigates the page away and destroys exactly the state the retry needs. A
// blocked popup surfaces as an honest `popupBlocked` error rather than escalating into data loss.
//
// Both entry points normalise through `withSecondFactorChallenge`. GCIP raises
// `auth/multi-factor-auth-required` on a REAUTHENTICATE operation just as it does on SIGN_IN (one
// resolver serves both — `MultiFactorResolver.resolveSignIn` switches on the operation type), so an
// enrolled user has to be able to finish the challenge HERE. Without it, enrolling a second factor
// would lock that user out of every step-up-gated action while leaving ordinary sign-in working —
// a lockout that only appears on the most sensitive screens.

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signOut,
  type Auth,
  type AuthProvider,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { AuthError } from './errors';
import { withSecondFactorChallenge } from './mfa';

/** Shown when a password re-auth is attempted for a user who has no email (provider-only account). */
const NO_PASSWORD_CREDENTIAL_MESSAGE =
  'This account has no password to confirm. Use the sign-in method you signed in with.';

/**
 * Normalise a re-authentication failure, naming the account on a mismatch.
 *
 * `auth/user-mismatch` is the ONE refusal these primitives exist to produce — the operator picked a
 * different account in the provider popup, or typed another account's password — and it is also the
 * one a generic message handles worst: "invalid credential" would send them to reset a password that
 * is perfectly fine. The account is knowable only here (the caller has the `User`; the error does
 * not), so the copy is specialised at this seam rather than in the shared kind table. Rendering the
 * signed-in user's own address back to them is safe — `@givmo/observability` scrubs emails from free
 * text before anything reaches telemetry.
 */
function namedMismatch(user: User, code: string | undefined): AuthError {
  if (user.email === null || user.email.length === 0)
    return new AuthError('userMismatch', { code });
  return new AuthError('userMismatch', {
    code,
    message: `That’s a different account — confirm as ${user.email} to continue.`,
  });
}

/**
 * The account a pending second factor belongs to, read before any code is spent, or `null` when
 * the error carries none. Firebase's own sign-in completion reads the same `localId` off the same
 * response (`UserImpl._fromIdTokenResponse`), so it is present whenever a challenge is.
 */
function pendingSecondFactorUid(error: unknown): string | null {
  const localId = (error as { customData?: { _serverResponse?: { localId?: unknown } } } | null)
    ?.customData?._serverResponse?.localId;
  return typeof localId === 'string' && localId.length > 0 ? localId : null;
}

function reauthenticationFailure(auth: Auth, user: User, error: unknown): AuthError {
  // Firebase checks the account only on a response WITHOUT a second factor (`_reauthenticate`
  // asserts `user.uid === localId` after it); a pending second factor is raised first, and
  // completing it swaps this session to whichever account answered the window. So check here.
  const pendingUid = pendingSecondFactorUid(error);
  if (pendingUid !== null && pendingUid !== user.uid) {
    return namedMismatch(user, 'auth/user-mismatch');
  }
  const expectedUid = user.uid;
  const normalised = withSecondFactorChallenge(auth, error, async (credential) => {
    // Belt: had the account still differed, the session is already the other account's.
    if (credential.user.uid !== expectedUid) {
      await signOut(auth);
      throw namedMismatch(user, 'auth/user-mismatch');
    }
    return credential;
  });
  if (normalised.kind !== 'userMismatch') return normalised;
  return namedMismatch(user, normalised.code);
}

/**
 * Re-authenticate `user` with their own password, refreshing the session's `auth_time`.
 *
 * Rejects with an {@link AuthError} — `invalidCredential` for a wrong password, `userMismatch` (named
 * to the signed-in account) when the password belongs to somebody else, `sessionExpired` once the
 * session itself is gone, and a `SecondFactorRequiredError` carrying the pending
 * {@link TotpChallenge} when the account has an enrolled second factor. Resolving that challenge
 * completes the re-authentication, so a caller that renders a code prompt needs no separate path.
 */
export function reauthenticateWithPassword(
  auth: Auth,
  user: User,
  password: string,
): Promise<UserCredential> {
  const email = user.email;
  if (email === null || email.length === 0) {
    return Promise.reject(new AuthError('unknown', { message: NO_PASSWORD_CREDENTIAL_MESSAGE }));
  }
  return reauthenticateWithCredential(user, EmailAuthProvider.credential(email, password)).catch(
    (error: unknown) => {
      throw reauthenticationFailure(auth, user, error);
    },
  );
}

/**
 * Re-authenticate `user` through their OAuth provider, refreshing the session's `auth_time`.
 *
 * Popup-only (see the header): a blocked popup rejects rather than escalating to a redirect that
 * would navigate away from the interrupted work. Choosing a DIFFERENT account in the popup rejects
 * with `userMismatch`, named to the signed-in account — the identity binding this primitive exists
 * for, and the failure a step-up surface is most likely to meet. A pending second factor arrives as
 * a `SecondFactorRequiredError`, exactly as above.
 */
export function reauthenticateWithProvider(
  auth: Auth,
  user: User,
  provider: AuthProvider,
): Promise<UserCredential> {
  return reauthenticateWithPopup(user, provider).catch((error: unknown) => {
    throw reauthenticationFailure(auth, user, error);
  });
}
